import type { LatLngExpression } from "leaflet"

// Themes
export const DEFAULT_THEME = "dark"

// Layout
export const SIDEBAR_WIDTH = 220
export const COLLAPSED_SIDEBAR_WIDTH = 55
export const NAVBAR_HEIGHT = 64
export const MAP_HEIGHT = "500px"
export const MAP_HEIGHT_COMPACT = "250px"
export const CARD_MAP_HEIGHT = "240px"
export const FOOTER_HEIGHT = 50
export const SECTIONS_BORDER_RADIUS = "0.25rem"
// Steps at sm because MUI's Container widens its own gutters there.
export const PLOT_CARD_PADDING_X = { xs: "1rem", sm: "1.5rem" }
export const PLOT_CARD_PADDING_BOTTOM = "0.5rem"
export const GRID_LAYOUT_GAP = 15
export const DASHBOARD_PADDING = "25px"
export const MD_ROW_HEIGHT = 130
export const SM_ROW_HEIGHT = 120

// Tables
export const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100]

// i18n
export const LANGUAGES = Object.freeze({
  en: Object.freeze({
    name: "English",
  }),
  es: Object.freeze({
    name: "Español",
  }),
})

export const LANGUAGE_KEYS = Object.keys(LANGUAGES)
LANGUAGE_KEYS.sort()
export const DEFAULT_LANGUAGE = "en"
export const LANGUAGES_AMOUNT = LANGUAGE_KEYS.length
export const DEFAULT_NAMESPACE = "translations"

// Alerts
export const ALERT_TIME = 60 * 1000
export const ALERT_SETTINGS_TIME = 10 * 60 * 1000
export const POPUP_TIME = 3 * 60 * 1000
export const SUCCESS_POPUP_TIME = 3 * 1000

// Snackbars
export const ALERT_SNACKBAR_TIME = 3 * 60 * 1000

// Sentrisense position refresh
export const SENTRISENSE_POSITION_TIME = 60 * 1000

// Devices refresh time (useDevices hook)
export const DEVICES_TABLE_TIME = 10 * 60 * 1000

// Device statuses
export const STATUSES_TIME = 60 * 1000

// Group devices refresh time (useDeviceGroups hook)
export const DEVICE_GROUPS_TIME = 20 * 60 * 1000

// Orgs refresh time (useOrgs hook)
export const ORGS_TIME = 30 * 60 * 1000

// Date range picker refresh
export const DATE_RANGE_TIME = 5 * 60 * 1000

// Loader timeout
export const LOADER_TIME = 2 * 1000

// Branding
export const IMAGOTYPE = "/images/sentrisense/logo-sentrisense-light.svg"
export const ISOTYPE = "/images/sentrisense/isotype-sentrisense-light.svg"

// URL
export const DEFAULT_TARGET = "/"

// USER
export const PASSWORD_MIN_LENGTH = 8
export const TOKEN_EXPIRATION_TIME = 30 // in minutes, set in the backend
export const TOKEN_REFRESH_THRESHOLD_TIME = 5 // in minutes
export const TOKEN_REFRESH_CHECK_TIME = 30 * 1000

// PERIOD OF TIME IN MILLISECONDS
export const ONE_DAY_MILLISECONDS = 60 * 60 * 24 * 1000
export const TEN_MINUTES = 60 * 10 * 1000

// REGEX
export const MIN_8_CHAR = /^.{8,}$/
export const AT_LEAST_ONE_UPPERCASE = /[A-Z]/
export const AT_LEAST_ONE_LOWERCASE = /[a-z]/
export const AT_LEAST_ONE_LOWERCASE_AND_ONE_UPPERCASE = /(?=.*[a-z])(?=.*[A-Z])/
export const AT_LEAST_ONE_NUMBER = /[0-9]/
export const AT_LEAST_ONE_SPECIAL_CHAR = /[^a-zA-Z0-9]/

// Password
export const PASSWORD = Object.freeze({
  // The strength score is between 0 and 4, being '0' the weakest score and '4' the
  // highest possible score.

  // If password strength score is less to 3, the password won't be accepted as valid
  MIN_VALID_STRENGTH: 3,
})

// user roles
export const USER_ROLES = Object.freeze({
  viewer: "viewer",
  editor: "editor",
  admin: "admin",
  owner: "owner",
})

export const USER_ROLES_WITHOUT_OWNER = Object.freeze({
  viewer: "viewer",
  editor: "editor",
  admin: "admin",
})

// device types
export const DEVICE_TYPES: readonly string[] = Object.freeze([
  "sentrisense",
  "weather_station",
  "virtual_powerline",
  "virtual_span",
  "virtual_tower",
])
export const NEW_DEVICE_BACKEND = "influx"

// MAP
// Zoom from which map labels are pinned. Below it they crowd, so hover only.
export const TOOLTIP_THRESHOLD = 13
export const DEFAULT_MAP_CENTER: LatLngExpression = [0, 0]
export const POLYLINE_HOVERED_WEIGHT = 6
export const POLYLINE_DEFAULT_WEIGHT = 4
export const POLYLINE_DEFAULT_OPACITY = 1
export const POLYLINE_NOT_HOVERED_OPACITY = 0.5

// oauth login providers
export const OAUTH_PROVIDERS = Object.freeze({
  GOOGLE: "google",
  MICROSOFT: "microsoft",
})

// sessionStorage key for saving the previous location
export const PREVIOUS_LOCATION_KEY = "previousLocation"

// plot csv
export const CSV_COLUMN_KEYS = [
  "id",
  "powerline_id",
  "name",
  "result",
  "_field",
  "_time",
  "_value",
]

// zoom
export const MIN_DRAG_PIXELS = 5
export const MIN_ZOOM_MINUTES = 3
export const ZOOM_PADDING_PERCENTAGE = 0.1
export const MIN_ZOOM_PADDING_MINUTES = 1
export const MIN_TOUCH_SPAN_PIXELS = 16

// floating buttons
export const SCROLL_TO_TOP_BUTTON_BOTTOM = 32
export const FLOATING_BUTTON_SPACING = 64 // FAB size (56) + gap (8)
export const EXIT_ZOOM_BUTTON_BOTTOM =
  SCROLL_TO_TOP_BUTTON_BOTTOM + FLOATING_BUTTON_SPACING // 96

// forecast
export const FORECAST_MAX_DAYS = 14
